import { CST_NAMES } from "./xmlScanData";

/**
 * Dados da tela de um fornecedor do XML Scan.
 * - Os números vêm do resumo da análise (`suppliers[]`).
 * - `documentos` é uma amostra de até 3 notas. O detalhe por item e o regime
 *   tributário saem do XML dessas notas, porque a API não os envia.
 * - Por campo: não conforme não tem CST nem cClassTrib; parcial tem, mas não tem
 *   base e valores. A regra vale exatamente nos totais da análise e nas amostras.
 * - Nota anterior a 2026 vem como "conforme" mesmo sem o grupo, porque ele ainda
 *   não era exigido: das 6 testadas, 2 não tinham o grupo. Das 12 de 2026, todas têm.
 * - A amostra nem sempre traz o problema: 14 dos 499 fornecedores com não conformes
 *   não têm nenhuma nota não conforme na amostra.
 */

export const CRT_NAMES = {
  1: "Simples Nacional",
  2: "Simples Nacional, excesso de sublimite",
  3: "Regime Normal",
  4: "MEI",
};
export const isSimples = (crt) => crt === "1" || crt === "2" || crt === "4";

const text = (node, tag) => node?.getElementsByTagName(tag)[0]?.textContent?.trim() || "";
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

// Campos da Reforma de uma nota, item a item
export function parseReformaXml(xml) {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) return null;
  const emit = doc.getElementsByTagName("emit")[0];
  const dest = doc.getElementsByTagName("dest")[0];
  const total = doc.getElementsByTagName("IBSCBSTot")[0];
  const items = [...doc.getElementsByTagName("det")].map((det, i) => {
    const group = det.getElementsByTagName("IBSCBS")[0] || null;
    return {
      n: det.getAttribute("nItem") || String(i + 1),
      name: text(det, "xProd"),
      ncm: text(det, "NCM"),
      cfop: text(det, "CFOP"),
      value: num(text(det, "vProd")),
      hasGroup: !!group,
      cst: text(group, "CST"),
      cClass: text(group, "cClassTrib"),
      base: group ? num(text(group, "vBC")) : null,
      ibs: group ? num(text(group, "vIBS")) : null,
      cbs: group ? num(text(group, "vCBS")) : null,
    };
  });
  return {
    crt: text(emit, "CRT"),
    uf: text(emit, "UF"),
    destName: text(dest, "xNome"),
    natOp: text(doc, "natOp"),
    total: num(text(doc, "vNF")),
    items,
    withGroup: items.filter((x) => x.hasGroup).length,
    base: total ? num(text(total, "vBCIBSCBS")) : null,
    ibs: total ? num(text(total, "vIBS")) : null,
    cbs: total ? num(text(total, "vCBS")) : null,
  };
}

// CNPJ da empresa do projeto selecionado: o XML Scan também lista as notas que ela emite
const projectRoot = () => {
  try {
    const project = JSON.parse(localStorage.getItem("vidal_current_project_data") || "{}");
    const digits = String(project.cnpj || "").replace(/\D/g, "");
    return digits.length === 14 ? digits.slice(0, 8) : "";
  } catch {
    return "";
  }
};

export function buildSupplierView(detail, supplierRaw) {
  const { batch, focus } = detail;
  const s = focus.suppliers.find((x) => x.raw === supplierRaw)
    || focus.suppliers.find((x) => x.cnpj === supplierRaw.cnpj && x.raw.id === supplierRaw.id)
    || focus.suppliers.find((x) => x.cnpj === supplierRaw.cnpj);
  if (!s) return null;

  const docs = (s.raw.documentos || []).map((d) => {
    const parsed = d.dhEmi ? Date.parse(d.dhEmi) : NaN;
    return {
      chave: d.chave || "",
      numero: String(d.numero || ""),
      status: d.conformidade || "",
      exempt: (d.anoEmissao || 0) > 0 && d.anoEmissao < 2026,
      date: Number.isNaN(parsed) ? null : new Date(parsed),
      missing: Array.isArray(d.missingFields) ? d.missingFields : [],
      issues: Array.isArray(d.validationIssues) ? d.validationIssues : [],
    };
  }).filter((d) => d.chave);
  const order = { nao_conforme: 0, parcial: 1, conforme: 2 };
  docs.sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3) || Number(a.exempt) - Number(b.exempt));

  // Campos do fornecedor: mesma regra dos totais da análise
  const required = docs.filter((d) => !d.exempt);
  const seletivo = required.length > 0 ? required.filter((d) => !d.missing.includes("CSTIS")).length / required.length : 0;
  const fieldPct = (group) => {
    if (s.total === 0) return 0;
    if (group.key === "id") return ((s.total - s.naoConformes) / s.total) * 100;
    if (group.key === "values") return (s.conformes / s.total) * 100;
    return seletivo * 100;
  };
  const groups = focus.groups.map((g) => {
    const pct = fieldPct(g);
    return { ...g, pct, fields: g.fields.map((x) => ({ ...x, pct })) };
  });

  const toRows = (map) => {
    const entries = Object.entries(map || {});
    const sum = entries.reduce((acc, [, v]) => acc + (Number(v) || 0), 0);
    return entries.map(([code, v]) => ({ code, value: Number(v) || 0, share: sum > 0 ? ((Number(v) || 0) / sum) * 100 : 0 })).sort((a, b) => b.value - a.value);
  };
  const cst = toRows(s.raw.cstUsados).map((r) => ({ ...r, name: CST_NAMES[r.code] || `CST ${r.code}` }));
  const classes = toRows(s.raw.cClassTribUsados);
  const finalities = toRows(s.raw.finNFeDistribuicao);
  const critical = s.raw.validationSummary?.CRITICAL || 0;
  const devolutions = Number(s.raw.finNFeDistribuicao?.["4"]) || 0;
  const finTotal = finalities.reduce((sum, x) => sum + x.value, 0);

  // Posição: mais problemas primeiro; empate fica com a mesma posição
  const position = (key) => (s[key] > 0 ? focus.suppliers.filter((x) => x[key] > s[key]).length + 1 : null);

  const digits = String(s.cnpj).replace(/\D/g, "");
  const root = digits.length === 14 ? digits.slice(0, 8) : "";
  const siblings = root
    ? focus.suppliers.filter((x) => x !== s && String(x.cnpj).replace(/\D/g, "").slice(0, 8) === root).sort((a, b) => b.naoConformes - a.naoConformes || b.total - a.total)
    : [];

  return {
    batch,
    s,
    docs,
    groups,
    cst,
    classes,
    finalities,
    critical,
    criticalPerDoc: s.naoConformes > 0 ? critical / s.naoConformes : 0,
    rankMissing: position("naoConformes"),
    rankPartial: position("parciais"),
    missingShare: batch.naoConformes > 0 ? (s.naoConformes / batch.naoConformes) * 100 : 0,
    partialShare: batch.parciais > 0 ? (s.parciais / batch.parciais) * 100 : 0,
    volumeShare: batch.read > 0 ? (s.total / batch.read) * 100 : 0,
    deltaToBatch: s.total > 0 ? s.pct - batch.pct : null,
    cst410: s.raw.cstUsados?.["410"] || 0,
    declaredCst: cst.reduce((sum, c) => sum + c.value, 0),
    devolutionShare: finTotal > 0 ? (devolutions / finTotal) * 100 : 0,
    sampleLacksProblem: s.naoConformes > 0 && docs.length > 0 && !docs.some((d) => d.status === "nao_conforme"),
    siblings,
    own: !!root && root === projectRoot(),
    supplierCount: focus.suppliers.length,
  };
}

// O que está errado e o que fazer, em linguagem direta
export function buildDiagnosis(v, regime) {
  const { s, cst410, criticalPerDoc, declaredCst, devolutionShare } = v;
  const simples = regime && isSimples(regime.crt);
  const count = (n) => n.toLocaleString("pt-BR");
  const mostly410 = s.parciais > 0 && cst410 >= s.parciais / 2;
  const regimeName = regime ? CRT_NAMES[regime.crt] : "";

  if (s.total === 0) return { tone: "flat", title: "Sem XMLs lidos", text: "Nenhum XML deste fornecedor foi lido nesta análise.", next: "" };
  if (s.conformes === s.total) {
    return { tone: "ok", title: "Todos os XMLs trazem os campos da Reforma", text: `Os ${count(s.total)} XMLs têm CST, classificação, base e valores de IBS e CBS.`, next: "Nenhuma ação necessária." };
  }

  const parts = [];
  if (devolutionShare >= 80) parts.push(devolutionShare >= 99.95 ? "Todas as notas deste fornecedor são devoluções." : `${devolutionShare.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% das notas deste fornecedor são devoluções.`);
  if (s.naoConformes > 0) {
    const perDoc = criticalPerDoc >= 2 ? ` São em média ${criticalPerDoc.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} itens sem o grupo por nota.` : "";
    parts.push(`${count(s.naoConformes)} ${s.naoConformes === 1 ? "XML não traz" : "XMLs não trazem"} o grupo IBS/CBS em nenhum item: faltam CST, classificação, base e valores.${perDoc}`);
  }
  if (s.parciais > 0) {
    parts.push(`${count(s.parciais)} ${s.parciais === 1 ? "XML traz" : "XMLs trazem"} CST e classificação, mas não base e valores.`);
  }

  const onlyImmune = s.parciais > 0 && cst410 >= s.parciais && s.naoConformes === 0;
  let next = "";
  let title = s.conformes === 0 || declaredCst === 0 ? "Não emite com os campos da Reforma" : "Emite com os campos em parte das notas";
  if (v.own) {
    title = "Notas emitidas pela própria empresa";
    next = `Este CNPJ é da empresa do projeto, então a correção é no sistema emissor interno, não com um fornecedor.${mostly410 ? " Parte dos parciais pode ser CST 410, que normalmente não leva base nem valor: confirme com o contador." : ""}`;
  } else if (onlyImmune) {
    title = "Pendência pode não ser real";
    next = "Os parciais podem ser CST 410 (imunidade e não incidência), que normalmente não leva base nem valor. Confirme com o contador antes de cobrar.";
  } else if (s.naoConformes > 0 && simples) {
    next = `A nota indica ${regimeName}, regime que tem regras próprias na transição. Confirme com o contador se o grupo já é exigido deste fornecedor antes de cobrar.`;
  } else if (s.naoConformes > 0 && regime) {
    next = `A nota indica ${regimeName}. Vale pedir ao fornecedor que atualize o sistema emissor para incluir o grupo IBS/CBS.${mostly410 ? " Parte dos parciais pode ser CST 410, que normalmente não leva base nem valor." : ""}`;
  } else if (s.naoConformes > 0) {
    next = "Vale pedir ao fornecedor que atualize o sistema emissor para incluir o grupo IBS/CBS.";
  } else if (cst410 > 0) {
    next = "Parte dos parciais pode ser CST 410, que normalmente não leva base nem valor. Confirme com o contador quais são pendência de fato.";
  } else {
    next = "Vale pedir ao fornecedor que informe base e valores de IBS e CBS nas notas.";
  }
  return { tone: s.naoConformes > 0 ? "bad" : "warn", title, text: parts.join(" "), next };
}
